"use client";

import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import { useUser } from "@clerk/nextjs";

type MembershipContextType = {
  isMember: boolean;
  isLoading: boolean;
  refresh: () => void;
};

const MembershipContext = createContext<MembershipContextType>({
  isMember: false,
  isLoading: false,
  refresh: () => {},
});

export function MembershipProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const [isMember, setIsMember] = useState(
    user?.publicMetadata?.has_active_membership === true,
  );
  const [isLoading, setIsLoading] = useState(false);
  const fetched = useRef(false);

  const fetchMembership = useCallback(() => {
    setIsLoading(true);
    fetch("/api/membership/status")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setIsMember(data.isActive === true);
        fetched.current = true;
      })
      .catch(() => {
        fetched.current = true;
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (user && !fetched.current) {
      fetchMembership();
    }
  }, [user, fetchMembership]);

  useEffect(() => {
    if (!user) {
      setIsMember(false);
      fetched.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!fetched.current && user) {
      setIsMember(user.publicMetadata?.has_active_membership === true);
    }
  }, [user?.publicMetadata?.has_active_membership, user]);

  return (
    <MembershipContext.Provider value={{ isMember, isLoading, refresh: fetchMembership }}>
      {children}
    </MembershipContext.Provider>
  );
}

export function useMembership() {
  return useContext(MembershipContext);
}
